// #6 Relatório Final de Apuração (REL-CMP-001) — aba "Relatório" do CaseDetailPage.
//
// Gating: SÓ na fase Decisão (state.kind === 'Decisao') — é a única em que atas +
// pareceres + fundamentação coexistem no state (Decisao→Recursal descarta oitivas
// e pareceres; ver READINESS / achado da perda de pareceres no Recursal). É o
// momento natural: o colegiado fecha a apuração e gera o relatório.
//
// Uma ÚNICA abertura de DEK (withCaseDek) decifra TUDO: ata+presentes de cada
// oitiva, justificativa de cada dispensa, justificativa de cada parecer, e a
// fundamentação da decisão. Falha por-item é isolada ("[falha ao decifrar]") —
// não derruba o relatório inteiro (paridade CaseDetailPage). Conteúdo cifrado
// nunca sai: só o PDF montado no browser. Inerte sem caseCrypto 'ok' (flag OFF).

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { decryptPayload } from '@aions/crypto';
import { Alert, Button } from '@aions/ui-primitives';
import type { DenunciaState } from '@aions/core';
import { apiFetch } from '../lib/auth-client';
import { withCaseDek, type CaseDekAccess } from '../lib/case-crypto';
import { base64ToBytes } from '../lib/bytes';
import { renderForm } from '../lib/pdf/form-pdf';
import {
  buildRelatorio,
  type RelatorioData,
  type RelatorioOitiva,
  type RelatorioParecer,
} from '../lib/pdf/form-relatorio';
import type { FormInput, MemberInfo } from '../lib/pdf/forms-common';

interface Props {
  caseId: string;
  categoria: string;
  state: DenunciaState;
  caseCrypto: CaseDekAccess;
}

const FALHA = '[falha ao decifrar]';

/** Decifra um campo, isolando falha por-item (não derruba o relatório). */
async function tryDecrypt(
  ct: string,
  nonce: string,
  dek: Uint8Array,
  caseId: string,
): Promise<string> {
  if (!ct) return '';
  try {
    return await decryptPayload(base64ToBytes(ct), base64ToBytes(nonce), dek, caseId);
  } catch {
    return FALHA;
  }
}

export function RelatorioPanel({ caseId, categoria, state, caseCrypto }: Props): JSX.Element {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Gating duro: só na fase Decisão (onde oitivas+pareceres+decisao coexistem).
  if (state.kind !== 'Decisao') {
    return (
      <Alert variant="info" role="status">
        {t('relatorio.fase_invalida')}
      </Alert>
    );
  }
  if (caseCrypto.status !== 'ok') {
    return (
      <Alert variant="info" role="status">
        {t('relatorio.indisponivel')}
      </Alert>
    );
  }
  const access = caseCrypto;
  const decisaoState = state; // narrow: kind === 'Decisao'

  async function handleGerar(): Promise<void> {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      let members: ReadonlyArray<MemberInfo> = [];
      const resp = await apiFetch(`/api/comite/cases/${caseId}/members`);
      if (resp.ok) members = ((await resp.json()) as { members: MemberInfo[] }).members;

      // UMA abertura de DEK decifra tudo (AAD=caseId).
      const rel = await withCaseDek(access, async (dek): Promise<RelatorioData> => {
        const oitivas: RelatorioOitiva[] = [];
        for (const o of decisaoState.oitivas) {
          if ('justificativaCiphertext' in o) {
            // DispensaOitiva
            oitivas.push({
              tipo: 'dispensa',
              data: o.autorizadoEm,
              porUlid: o.autorizadoPor,
              conteudo: await tryDecrypt(o.justificativaCiphertext, o.nonce, dek, caseId),
            });
          } else {
            // Oitiva
            oitivas.push({
              tipo: 'oitiva',
              data: o.registeredAt,
              porUlid: o.registeredBy,
              conteudo: await tryDecrypt(o.ataCiphertext, o.nonce, dek, caseId),
              presentes: await tryDecrypt(o.attendeesCiphertext, o.attendeesNonce, dek, caseId),
            });
          }
        }
        const pareceres: RelatorioParecer[] = [];
        for (const p of decisaoState.pareceres) {
          pareceres.push({
            memberId: p.memberId,
            voto: p.voto,
            justificativa: await tryDecrypt(p.justificativaCiphertext, p.nonce, dek, caseId),
          });
        }
        const fundamentacao = await tryDecrypt(
          decisaoState.decisao.justificativaCiphertext,
          decisaoState.decisao.nonce,
          dek,
          caseId,
        );
        return { oitivas, pareceres, fundamentacao };
      });

      const input: FormInput = {
        caseId,
        categoria,
        createdWeek: '',
        state,
        attachments: [],
        members,
      };
      const blob = renderForm(buildRelatorio(input, rel));
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `relatorio-final-${caseId.slice(0, 12)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setError('relatorio_failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fade-in" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <p style={{ color: 'var(--color-text-secondary)', margin: 0 }}>{t('relatorio.intro')}</p>
      {error !== null && (
        <div className="alert alert-error" role="alert">
          {t(`relatorio.errors.${error}`, { defaultValue: t('relatorio.errors.relatorio_failed') })}
        </div>
      )}
      <div>
        <Button variant="primary" disabled={busy} onClick={() => void handleGerar()}>
          {busy ? t('relatorio.gerando') : t('relatorio.gerar')}
        </Button>
        <p style={{ color: 'var(--color-text-muted)', fontSize: '0.8rem', marginTop: 8 }}>
          {t('relatorio.nota')}
        </p>
      </div>
    </div>
  );
}
